import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import {
  callSaradiumBackend,
} from "../../lib/appwrite";

import {
  defaultStyle,
  isStyleAvailable,
  type LiquidPalette,
  type Plan,
  type UiStyle,
} from "./design.types";

type DesignContextValue = {
  plan: Plan;
  developer: boolean;
  style: UiStyle;
  liquidPalette: LiquidPalette;
  loading: boolean;
  setStyle: (style: UiStyle) => void;
  setLiquidPalette: (
    palette: LiquidPalette,
  ) => void;
  refreshEntitlement: () => Promise<void>;
};

const DesignContext =
  createContext<DesignContextValue | null>(null);

const STYLE_KEY = "saradium-ui-style";
const PALETTE_KEY = "saradium-liquid-palette";

function storedStyle(): UiStyle {
  const value = localStorage.getItem(STYLE_KEY);

  if (
    value === "SPATIAL_FLOW" ||
    value === "SOFT_NEUMORPHISM" ||
    value === "LIQUID_GLASS"
  ) {
    return value;
  }

  return defaultStyle();
}

function storedPalette(): LiquidPalette {
  const value = localStorage.getItem(PALETTE_KEY);

  if (
    value === "AURORA" ||
    value === "CYAN_LAGOON" ||
    value === "COFFEE_MILK"
  ) {
    return value;
  }

  return "AURORA";
}

function cssValue(value: string): string {
  return value.toLowerCase().replaceAll("_", "-");
}

export function DesignProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [plan, setPlan] =
    useState<Plan>("FREE");

  const [developer, setDeveloper] =
    useState(false);

  const [style, setStyleState] =
    useState<UiStyle>(storedStyle);

  const [liquidPalette, setPaletteState] =
    useState<LiquidPalette>(storedPalette);

  const [loading, setLoading] =
    useState(true);

  async function refreshEntitlement() {
    setLoading(true);

    try {
      const entitlement =
        await callSaradiumBackend<{
          plan?: Plan;
          developer?: boolean;
        }>({
          action: "getMyEntitlement",
        });

      setPlan(
        entitlement.plan === "PREMIUM" ||
          entitlement.plan === "PREMIUM_PLUS"
          ? entitlement.plan
          : "FREE",
      );

      setDeveloper(Boolean(entitlement.developer));
    } catch {
      setPlan("FREE");
      setDeveloper(false);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refreshEntitlement();
  }, []);

  useEffect(() => {
    if (
      !developer &&
      !isStyleAvailable(style, plan)
    ) {
      setStyleState(defaultStyle());
    }
  }, [developer, plan, style]);

  useEffect(() => {
    document.documentElement.dataset.uiStyle =
      cssValue(style);

    document.documentElement.dataset.liquidPalette =
      cssValue(liquidPalette);

    localStorage.setItem(STYLE_KEY, style);

    localStorage.setItem(
      PALETTE_KEY,
      liquidPalette,
    );
  }, [style, liquidPalette]);

  function setStyle(nextStyle: UiStyle) {
    if (
      !developer &&
      !isStyleAvailable(nextStyle, plan)
    ) {
      return;
    }

    setStyleState(nextStyle);
  }

  function setLiquidPalette(
    nextPalette: LiquidPalette,
  ) {
    const allowed =
      developer || plan === "PREMIUM_PLUS";

    if (
      style !== "LIQUID_GLASS" ||
      !allowed
    ) {
      return;
    }

    setPaletteState(nextPalette);
  }

  const value = useMemo(
    () => ({
      plan,
      developer,
      style,
      liquidPalette,
      loading,
      setStyle,
      setLiquidPalette,
      refreshEntitlement,
    }),
    [
      plan,
      developer,
      style,
      liquidPalette,
      loading,
    ],
  );

  return (
    <DesignContext.Provider value={value}>
      {children}
    </DesignContext.Provider>
  );
}

export function useDesign() {
  const context = useContext(DesignContext);

  if (!context) {
    throw new Error(
      "useDesign must be used inside DesignProvider.",
    );
  }

  return context;
}